import { useCallback, useMemo } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import { formatDecimal, formatPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { KnowYourCustomerData } from '@blisspointmedia/bpm-types/dist/KnowYourCustomer';
import { CbsaMap } from '@Components/CbsaMap';
import { ChartContainer } from '@Components/ChartContainer';
import { Placement } from '@Components/designConstants';
import { GradientLegend } from '@Components/GradientLegend';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { WidgetContainer } from '@Components/WidgetContainer';
import { CBSA_TO_MSA_MAP } from '@src/Components/CbsaMap/cbsa-map-constants';
import {
  getColorForIndexedVolume,
  getThresholdBinsWithStdDevNonAdjusted
} from '@blisspointmedia/bpm-types/dist/brandHealthMetricsUtils';
import { getThresholdBinsWithStdDev } from '@src/utils/charts/maps';
import { ConvergentColorMap6, DivergentColorMap8 } from '../../utils/colors';
import { downloadJSONToCSV } from '../../utils/download-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { TabKey, ViewOptions } from './customer-insights-constants';
import { CustomerInsightsTable } from './CustomerInsightsTable';

interface CustomerInsightsGeoGroupProps {
  data: KnowYourCustomerData[];
  totalPopulation: number;
  header: string;
  tab: TabKey;
  graphView: ViewOptions;
  dataTestId: string;
}

export const CustomerInsightsGeoGroup = ({
  data,
  totalPopulation,
  header,
  tab,
  graphView,
  dataTestId
}: CustomerInsightsGeoGroupProps) => {
  const { cid, name } = useCompanyInfo();
  const companyName = cid === 'tinuititest' ? 'Tinuiti' : name;

  const knownPopulation = useMemo(() => {
    const unknownPop = data.find(row => row.levels === 'Unknown')?.clientPopulation ?? 0;
    return totalPopulation - unknownPop;
  }, [data, totalPopulation]);

  const getMetricFromData = useCallback(
    (row: KnowYourCustomerData, tabInput: TabKey): number => {
      switch (tabInput) {
        case TabKey.CUSTOMER_BASE:
          return row.clientPopulation / knownPopulation;
        case TabKey.INDEX:
          return row.clientPopulationFraction / row.tuPopulationFraction;
        case TabKey.PENETRATION:
          return row.clientPopulation / row.tuPopulation;
        default:
          return -1;
      }
    },
    [knownPopulation]
  );

  const mappedData: Record<string, KnowYourCustomerData> = useMemo(
    () => Object.fromEntries(data.map(row => [row.levels, row])),
    [data]
  );

  const bins = useMemo(() => {
    const values = data.map(val =>
      getMetricFromData(mappedData[val.levels] ?? mappedData[CBSA_TO_MSA_MAP[val.levels]], tab)
    );
    return tab === TabKey.INDEX
      ? getThresholdBinsWithStdDev(values)
      : getThresholdBinsWithStdDevNonAdjusted(values);
  }, [data, getMetricFromData, mappedData, tab]);

  const colorMap = tab === TabKey.INDEX ? DivergentColorMap8 : ConvergentColorMap6;

  const explanationText = useMemo(() => {
    switch (tab) {
      case TabKey.CUSTOMER_BASE:
        return '((Number of customers) /\n(ingested emails)) per MSA';
      case TabKey.INDEX:
        return '((percent of our customers) /\n(percent of total population)) per MSA';
      case TabKey.PENETRATION:
        return '((Number of customers) /\n(total population)) per MSA';
      default:
        return '';
    }
  }, [tab]);

  const widgetTitle = useMemo(() => {
    switch (tab) {
      case TabKey.CUSTOMER_BASE:
        return 'Customer Population per MSA';
      case TabKey.INDEX:
        return 'Customer Index per MSA';
      case TabKey.PENETRATION:
        return 'Customer Penetration per MSA';
      default:
        return '';
    }
  }, [tab]);

  const tooltipCopy = useMemo(() => {
    switch (tab) {
      case TabKey.CUSTOMER_BASE:
        return 'The percentage of your customer base residing in each MSA.';
      case TabKey.INDEX:
        return 'The relative percentage of your customer base residing in each MSA, as compared to the percentage of the total U.S. market.';
      case TabKey.PENETRATION:
        return 'The percentage of the total MSA population that is represented in your customer base.';
      default:
        return '';
    }
  }, [tab]);

  return (
    <WidgetContainer dataTestId={dataTestId} header={header} collapsible headerCollapsibleOnClick>
      <div className='customer-insights-group full-width'>
        <ChartContainer
          title={widgetTitle}
          rightActions={
            <>
              <InfoTooltip placement={Placement.BOTTOM_END}>
                {tooltipCopy}
                <span className='tooltip-calculation'>Calculated as: {explanationText}</span>
              </InfoTooltip>
              <IconButton
                data-testid='geo-group-download'
                onClick={() => {
                  const formattedData = data.map(row => ({
                    'MSA Name': row.levels,
                    [`${companyName}'s Population`]: Math.round(row.clientPopulation),
                    [`${companyName}'s Distribution`]: row.clientPopulationFraction,
                    'US Market': Math.round(row.tuPopulation),
                    'US Market Distribution': row.tuPopulationFraction,
                    Index: getMetricFromData(row, TabKey.INDEX),
                    Penetration: getMetricFromData(row, TabKey.PENETRATION)
                  }));
                  const date = new Date();
                  downloadJSONToCSV(
                    formattedData,
                    `CustomerInsights ${companyName} MSAs ${date.toLocaleString()}`
                  );
                }}
              >
                <MdOutlineFileDownload />
              </IconButton>
            </>
          }
        >
          {graphView === ViewOptions.SHOW_GRAPH && (
            <TabGeoMap
              tabKey={tab}
              bins={bins}
              colorMap={colorMap}
              getMetricFromData={getMetricFromData}
              mappedData={mappedData}
            />
          )}
          {graphView === ViewOptions.SHOW_TABLE && (
            <div className='geo-table'>
              <CustomerInsightsTable tab={tab} fieldData={data} />
            </div>
          )}
        </ChartContainer>
      </div>
    </WidgetContainer>
  );
};

interface TabGeoMapProps {
  tabKey: TabKey;
  bins: number[];
  colorMap: string[];
  getMetricFromData: (row: KnowYourCustomerData, tab: TabKey) => number;
  mappedData: Record<string, KnowYourCustomerData>;
}

const TabGeoMap = ({ tabKey, bins, colorMap, getMetricFromData, mappedData }: TabGeoMapProps) => {
  const formatLabel = useCallback(
    (val: number) => (tabKey !== TabKey.INDEX ? formatPercent(val, 2) : formatDecimal(val, 2)),
    [tabKey]
  );

  const getRenderColor = useCallback(
    (cbsa: string): string => {
      const metricVal = getMetricFromData(
        mappedData[cbsa] ?? mappedData[CBSA_TO_MSA_MAP[cbsa]],
        tabKey
      );
      if (metricVal == null) {
        return 'transparent';
      }
      return getColorForIndexedVolume(metricVal, bins, colorMap);
    },
    [bins, colorMap, getMetricFromData, mappedData, tabKey]
  );

  const renderTooltip = useCallback(
    (cbsa: string) => {
      const metricVal = getMetricFromData(
        mappedData[cbsa] ?? mappedData[CBSA_TO_MSA_MAP[cbsa]],
        tabKey
      );
      return (
        <div>
          {cbsa}: {formatLabel(metricVal)}
        </div>
      );
    },
    [formatLabel, getMetricFromData, mappedData, tabKey]
  );

  return (
    <div className='geo-map'>
      <GradientLegend
        bins={bins}
        useConvergent={tabKey !== TabKey.INDEX}
        formatLabel={formatLabel}
      />
      <div className='cbsa-map-container'>
        <CbsaMap renderColor={getRenderColor} renderTooltip={renderTooltip} />
      </div>
    </div>
  );
};
